import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  useTheme,
  alpha,
  LinearProgress,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Build as BuildIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';

const Equipos = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);

  const columns = [
    { field: 'codigo', headerName: 'Código', width: 120, renderCell: (params) => (
      <Typography variant="body2" fontWeight={500}>{params.value}</Typography>
    )},
    { field: 'descripcion', headerName: 'Equipo', width: 200, flex: 1 },
    { field: 'tipo', headerName: 'Tipo', width: 130, renderCell: (params) => (
      <Chip label={params.value} size="small" variant="outlined" />
    )},
    { field: 'ubicacion', headerName: 'Ubicación', width: 150 },
    { field: 'horasOperacion', headerName: 'Horas Op.', width: 100, align: 'right', headerAlign: 'right' },
    { field: 'proximoMant', headerName: 'Próx. Mant.', width: 120, renderCell: (params) => {
      const diasRestantes = parseInt(params.value);
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {diasRestantes <= 7 && <WarningIcon fontSize="small" color="warning" />}
          <Typography variant="body2">{params.value} días</Typography>
        </Box>
      );
    }},
    { field: 'estado', headerName: 'Estado', width: 130, renderCell: (params) => (
      <Chip
        icon={params.value === 'Operativo' ? <CheckIcon /> : <WarningIcon />}
        label={params.value}
        size="small"
        color={params.value === 'Operativo' ? 'success' : params.value === 'En Mantto' ? 'warning' : 'error'}
      />
    )},
    { field: 'acciones', headerName: 'Acciones', width: 120, sortable: false, renderCell: () => (
      <Box>
        <IconButton size="small"><ViewIcon fontSize="small" /></IconButton>
        <IconButton size="small"><EditIcon fontSize="small" /></IconButton>
      </Box>
    )},
  ];

  const rows = [
    { id: 1, codigo: 'BOP-001', descripcion: 'Blowout Preventer Principal', tipo: 'Seguridad', ubicacion: 'Pozo XY-123', horasOperacion: 4520, proximoMant: '15', estado: 'Operativo' },
    { id: 2, codigo: 'GR-012', descripcion: 'Grúa Telescópica 50T', tipo: 'Izaje', ubicacion: 'Pozo AB-456', horasOperacion: 2890, proximoMant: '5', estado: 'Operativo' },
    { id: 3, codigo: 'BOM-003', descripcion: 'Bomba de Lodos #3', tipo: 'Perforación', ubicacion: 'Taller Central', horasOperacion: 6200, proximoMant: '0', estado: 'En Mantto' },
    { id: 4, codigo: 'GEN-008', descripcion: 'Generador 500KVA', tipo: 'Energía', ubicacion: 'Pozo XY-123', horasOperacion: 3450, proximoMant: '22', estado: 'Operativo' },
    { id: 5, codigo: 'TOP-002', descripcion: 'Top Drive Sistema', tipo: 'Perforación', ubicacion: 'Pozo AB-456', horasOperacion: 1850, proximoMant: '30', estado: 'Operativo' },
  ];

  const resumenStats = [
    { label: 'Total Equipos', value: '42', color: theme.palette.primary.main },
    { label: 'Operativos', value: '38', color: theme.palette.success.main },
    { label: 'En Mantenimiento', value: '3', color: theme.palette.warning.main },
    { label: 'Fuera de Servicio', value: '1', color: theme.palette.error.main },
  ];

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Equipos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Control de equipos y maquinaria
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
          Nuevo Equipo
        </Button>
      </Box>

      {/* Resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {resumenStats.map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={700} color={stat.color}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Alerta Mantenimientos */}
      <Card sx={{ mb: 3, bgcolor: alpha(theme.palette.warning.main, 0.05), border: `1px solid ${theme.palette.warning.main}` }}>
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WarningIcon color="warning" />
            <Typography variant="body1" fontWeight={500}>
              2 equipos requieren mantenimiento en los próximos 7 días
            </Typography>
            <Button size="small" variant="outlined" color="warning" sx={{ ml: 'auto' }}>
              Ver Detalle
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent>
          <Box sx={{ height: 450 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={10}
              disableSelectionOnClick
              slots={{ toolbar: GridToolbar }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nuevo Equipo</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Código" />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select label="Tipo">
                  <MenuItem value="seguridad">Seguridad</MenuItem>
                  <MenuItem value="perforacion">Perforación</MenuItem>
                  <MenuItem value="izaje">Izaje</MenuItem>
                  <MenuItem value="energia">Energía</MenuItem>
                  <MenuItem value="transporte">Transporte</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descripción" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Marca" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Modelo" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Número de Serie" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Año de Fabricación" type="number" />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Ubicación</InputLabel>
                <Select label="Ubicación">
                  <MenuItem value="1">Pozo XY-123</MenuItem>
                  <MenuItem value="2">Pozo AB-456</MenuItem>
                  <MenuItem value="3">Taller Central</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Horas de Operación Inicial" type="number" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Equipos;
